import { z } from 'zod'
import { Condition } from './conditions.ts'
import { GrapheParcours } from './graphe.ts'

/**
 * Le document de configuration. Un service = un document. Six sections pour les
 * six objets de la mission. Ce fichier est le contrat : le noyau, les règles,
 * le processus, la page publique et les outils du modèle en descendent tous.
 *
 * Le vocabulaire exposé est celui du métier et il est en français, y compris
 * dans les clés. Le code autour est en anglais.
 */

export const Cle = z
  .string()
  .regex(
    /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/,
    'clé en minuscules, chiffres et tirets',
  )
export const Heure = z
  .string()
  .regex(/^([01]\d|2[0-4]):[0-5]\d$/, 'heure HH:MM, 24:00 accepté')
export const Date_ = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'date AAAA-MM-JJ')
export const Jour = z.enum([
  'lundi',
  'mardi',
  'mercredi',
  'jeudi',
  'vendredi',
  'samedi',
  'dimanche',
])
export type Jour = z.infer<typeof Jour>
export const Cents = z.number().int().min(0)

export const CompteGL = z
  .object({
    compte_debit: z.string().min(1).max(50),
    compte_credit: z.string().min(1).max(50),
    traitement: z.enum(['produit', 'differe', 'depot']).default('produit'),
    taxes: z.record(z.string(), z.string()).default({}),
  })
  .strict()
export type CompteGL = z.infer<typeof CompteGL>

// ─── Ressources ──────────────────────────────────────────────────────────────

export const Ressource = z
  .object({
    cle: Cle,
    nom: z.string().min(1),
    /** Identifiant stable attribué par le serveur. Le modèle ne le fabrique jamais. */
    id: z.string().optional(),
    description: z.string().optional(),
    /** Occupations simultanées tolérées. 1 = exclusive. */
    capacite: z.number().int().min(1).default(1),
    /** Une ressource exclusive refuse tout chevauchement, quelle que soit la quantité. */
    exclusif: z.boolean().default(true),
    /** Ressources interchangeables : le citoyen n'a pas à choisir laquelle. */
    groupe: Cle.optional(),
    lieu: z.string().optional(),
  })
  .strict()
export type Ressource = z.infer<typeof Ressource>

// ─── Règles ──────────────────────────────────────────────────────────────────

const communRegle = {
  cle: Cle,
  /** Ressources visées. Vide = tout le service. */
  portee: z
    .object({
      ressources: z.array(Cle).optional(),
      groupes: z.array(Cle).optional(),
    })
    .strict()
    .optional(),
  /** La règle ne s'applique que si cette condition est vraie. */
  quand: Condition.optional(),
  /** Phrase montrée au citoyen quand la règle refuse. L'explicateur en fabrique une si absente. */
  message: z.string().optional(),
}

export const PlageHoraire = z
  .object({ jours: z.array(Jour).min(1), de: Heure, a: Heure })
  .strict()

export const Fermeture = z
  .object({
    date: Date_,
    de: Heure.optional(),
    a: Heure.optional(),
    motif: z.string().min(1),
    recurrence: z.enum(['aucune', 'annuelle']).default('aucune'),
  })
  .strict()

/** Bornes civiles inclusives, répétées chaque année dans le fuseau du service. */
export const MoisJour = z.string().regex(/^\d{2}-\d{2}$/, 'date MM-JJ').refine((valeur) => {
  const [mois, jour] = valeur.split('-').map(Number)
  const date = new Date(Date.UTC(2000, mois! - 1, jour!))
  return date.getUTCMonth() === mois! - 1 && date.getUTCDate() === jour
}, 'date MM-JJ invalide')

export const SaisonAnnuelle = z.object({ du: MoisJour, au: MoisJour }).strict()

export const RegleHoraire = z
  .object({
    ...communRegle,
    type: z.literal('horaire'),
    plages: z.array(PlageHoraire).min(1),
    fermetures: z.array(Fermeture).default([]),
    saison_annuelle: SaisonAnnuelle.optional(),
  })
  .strict()

export const RegleDuree = z
  .object({
    ...communRegle,
    type: z.literal('duree'),
    minimum_minutes: z.number().int().min(1).optional(),
    maximum_minutes: z.number().int().min(1).optional(),
    /** Granularité des durées offertes. */
    pas_minutes: z.number().int().min(1).default(30),
    /** Les débuts tombent sur un multiple de ceci depuis minuit local. */
    alignement_minutes: z.number().int().min(1).default(30),
  })
  .strict()

export const RegleAvance = z
  .object({
    ...communRegle,
    type: z.literal('avance'),
    /** Délai minimal entre maintenant et le début. */
    minimum_minutes: z.number().int().min(0).optional(),
    /** Horizon d'ouverture du calendrier. */
    maximum_jours: z.number().int().min(1).optional(),
  })
  .strict()

export const PlageTarif = z
  .object({
    jours: z.array(Jour).min(1),
    de: Heure,
    a: Heure,
    montant_cents: Cents,
  })
  .strict()

export const RegleTarif = z
  .object({
    ...communRegle,
    type: z.literal('tarif'),
    /** L'unité de facturation. `heure` est proratisé au pas de la règle de durée. */
    unite: z
      .enum(['heure', 'reservation', 'jour', 'personne', 'unite'])
      .default('heure'),
    montant_cents: Cents,
    item_id: z.string().optional(),
    par_unite_reservee: z.boolean().optional(),
    compte_gl: CompteGL.optional(),
    /** Tarifs particuliers par jour et par heure. La première plage qui couvre gagne. */
    plages: z.array(PlageTarif).default([]),
    /** Ajustements en pourcentage, appliqués après le montant de base. */
    modificateurs: z
      .array(
        z
          .object({
            cle: Cle,
            quand: Condition,
            pourcentage: z.number(),
            libelle: z.string().min(1),
          })
          .strict(),
      )
      .default([]),
    /** Ordre d'examen. La première règle de tarif applicable gagne, la plus petite priorité d'abord. */
    priorite: z.number().int().default(100),
    libelle: z.string().optional(),
  })
  .strict()

export const RegleDepot = z
  .object({
    ...communRegle,
    type: z.literal('depot'),
    montant_cents: Cents,
    mode: z
      .enum(['retenue', 'encaisse', 'retenue_sinon_encaisse'])
      .default('retenue_sinon_encaisse'),
    rembourse_si_non_aboutie: z.boolean().default(true),
  })
  .strict()

export const PalierAnnulation = z
  .object({
    /** Palier actif si l'annulation arrive au moins ce nombre d'heures avant le début. */
    au_moins_heures_avant: z.number().int().min(0),
    remboursement_tarif_pourcentage: z.number().min(0).max(100),
    remboursement_depot_pourcentage: z.number().min(0).max(100),
  })
  .strict()

export const RegleAnnulation = z
  .object({
    ...communRegle,
    type: z.literal('annulation'),
    /** Le citoyen peut annuler lui-même depuis son lien de suivi. */
    annulable_par_le_citoyen: z.boolean().default(true),
    paliers: z.array(PalierAnnulation).min(1),
  })
  .strict()

export const RegleQuota = z
  .object({
    ...communRegle,
    type: z.literal('quota'),
    maximum: z.number().int().min(1),
    par: z.enum(['demandeur', 'organisation_demandeuse']).default('demandeur'),
    fenetre: z
      .object({
        type: z.enum(['civile', 'glissante']),
        unite: z.enum(['jour', 'semaine', 'mois']),
        nombre: z.number().int().min(1).default(1),
      })
      .strict(),
    /** Les tenues provisoires comptent, sinon on ouvre une file d'attente déguisée. */
    compte_provisoires: z.boolean().default(true),
    /** Compter aussi les heures plutôt que les réservations. */
    unite_comptee: z.enum(['reservations', 'heures']).default('reservations'),
  })
  .strict()

export const RegleTampon = z
  .object({
    ...communRegle,
    type: z.literal('tampon'),
    avant_minutes: z.number().int().min(0).default(0),
    apres_minutes: z.number().int().min(0).default(0),
  })
  .strict()

export const RegleConflit = z
  .object({
    ...communRegle,
    type: z.literal('conflit'),
    /** Ces ressources ne peuvent pas être occupées en même temps. Symétrisé à la validation. */
    ressources: z.array(Cle).min(2),
  })
  .strict()

export const RegleAdmissibilite = z
  .object({
    ...communRegle,
    type: z.literal('admissibilite'),
    /** La demande n'est recevable que si ceci est vrai. */
    exige: Condition,
  })
  .strict()

export const RegleCondition = z
  .object({
    ...communRegle,
    type: z.literal('condition'),
    exige: Condition,
  })
  .strict()

export const Regle = z.discriminatedUnion('type', [
  RegleHoraire,
  RegleDuree,
  RegleAvance,
  RegleTarif,
  RegleDepot,
  RegleAnnulation,
  RegleQuota,
  RegleTampon,
  RegleConflit,
  RegleAdmissibilite,
  RegleCondition,
])
export type Regle = z.infer<typeof Regle>
export type TypeRegle = Regle['type']

// ─── Questions posées au citoyen ─────────────────────────────────────────────

export const Champ = z
  .object({
    cle: Cle,
    libelle: z.string(),
    type: z.enum([
      'texte',
      'texte_long',
      'nombre',
      'oui_non',
      'choix',
      'courriel',
      'telephone',
      'date',
      'fichier',
    ]),
    options: z
      .array(z.object({ valeur: z.string(), libelle: z.string() }).strict())
      .default([]),
    obligatoire: z.boolean().default(true),
    aide: z.string().optional(),
    minimum: z.number().optional(),
    maximum: z.number().optional(),
    entier: z.boolean().optional(),
    /** Un champ fichier accepte plusieurs pièces, chacune limitée par le serveur. */
    nombre_fichiers_max: z.number().int().min(1).max(10).optional(),
    /** Le champ n'apparaît que si la condition est vraie. */
    si: Condition.optional(),
    /** Déclaration sur l'honneur : ajoute la mention et la note de vérification. */
    sur_lhonneur: z.boolean().default(false),
  })
  .strict()
export type Champ = z.infer<typeof Champ>

// ─── Blocs de processus ──────────────────────────────────────────────────────

const communBloc = {
  cle: Cle,
  si: Condition.optional(),
  titre: z.string().optional(),
}

export const BlocCreneau = z
  .object({
    ...communBloc,
    bloc: z.literal('creneau'),
    /** Le citoyen choisit sa ressource, sinon le noyau alloue dans le groupe. */
    choix_de_la_ressource: z.boolean().default(false),
    /** Durée de la tenue provisoire une fois l'identité connue. */
    tenue_minutes: z.number().int().min(1).default(15),
    /** Une plage datée, pour une inscription à une séance unique. */
    periode_fixe: z
      .object({
        debut_utc: z.string().datetime(),
        fin_utc: z.string().datetime(),
      })
      .strict()
      .optional(),
    /** Champ numérique déjà collecté indiquant combien de places retenir. */
    quantite_fait: z
      .string()
      .regex(/^(demandeur|reponses)\.[a-z0-9-]+$/)
      .optional(),
    quantite_maximum: z.number().int().min(1).max(1000).default(1),
  })
  .strict()

export const BlocIdentite = z
  .object({
    ...communBloc,
    bloc: z.literal('identite'),
    demande_organisation: z.boolean().default(false),
    demande_telephone: z.boolean().default(false),
    demande_adresse: z.boolean().default(false),
    champs: z.array(Champ).default([]),
  })
  .strict()

export const BlocQuestions = z
  .object({
    ...communBloc,
    bloc: z.literal('questions'),
    champs: z.array(Champ).default([]),
  })
  .strict()

/** L'identité et la version choisies sont copiées dans le parcours publié. */
export const IdentiteDocument = z
  .object({
    nom: z
      .string()
      .min(1)
      .max(160)
      .refine(
        (s) => !/[\r\n]/.test(s),
        'Le nom doit tenir sur une ligne de saisie.',
      ),
    coordonnees: z
      .string()
      .max(1200)
      .refine(
        (s) => s.split('\n').length <= 8,
        'Les coordonnées peuvent contenir au plus huit lignes.',
      )
      .default(''),
    couleur: z
      .string()
      .regex(/^#[0-9a-fA-F]{6}$/)
      .default('#1d4ed8'),
    pied_page: z
      .string()
      .max(240)
      .refine(
        (s) => !/[\r\n]/.test(s),
        'Le pied de page doit tenir sur une ligne de saisie.',
      )
      .default(''),
    logo_jpeg_base64: z
      .string()
      .max(600000)
      .regex(/^\/9j\/[A-Za-z0-9+/]*={0,2}$/)
      .optional(),
    fond_entete: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
  })
  .strict()
export const DocumentContrat = z
  .object({
    modele_id: z.string().max(100),
    version_id: z.string().max(100),
    titre: z.string().min(1).max(1000),
    identite: IdentiteDocument,
  })
  .strict()

export const OptionOffre = z
  .object({
    cle: Cle,
    item_id: z.string().min(1),
    nom: z.string().min(1),
    description: z.string().optional(),
    montant_cents: Cents.max(100000000),
    unite: z
      .enum(['reservation', 'heure', 'jour', 'unite', 'personne'])
      .default('unite'),
    quantite_maximum: z.number().int().min(1).max(1000).default(1),
    ressource_id: z.string().optional(),
    compte_gl: CompteGL.optional(),
    si: Condition.optional(),
  })
  .strict()
export const BlocOffres = z
  .object({
    ...communBloc,
    bloc: z.literal('offres'),
    quantite_principale_maximum: z.number().int().min(1).max(1000).optional(),
    options: z.array(OptionOffre).max(100).default([]),
  })
  .strict()

export const BlocContrat = z
  .object({
    ...communBloc,
    bloc: z.literal('contrat'),
    /** Gabarit en texte, avec des {{jetons}} remplis par le serveur. */
    gabarit: z.string(),
    /** Clauses approuvées/configurées pour un projet de série admin non signé. */
    clauses_serie: z.string().max(25000).optional(),
    document: DocumentContrat.optional(),
    exige_lecture: z.boolean().default(true),
  })
  .strict()

export const BlocSignature = z
  .object({
    ...communBloc,
    bloc: z.literal('signature'),
    mode: z.enum(['interne']).default('interne'),
    mention: z
      .string()
      .default(
        "Je certifie que les renseignements fournis sont exacts et j'accepte les conditions du contrat.",
      ),
  })
  .strict()

export const BlocApprobation = z
  .object({
    ...communBloc,
    bloc: z.literal('approbation'),
    responsable_courriel: z.string().email().optional(),
    /** Sans décision passé ce délai, la demande est refusée et le citoyen est prévenu. */
    delai_heures: z.number().int().min(1).default(72),
    /** La demande tient le créneau pendant l'attente. */
    bloque_le_creneau: z.boolean().default(true),
  })
  .strict()

export const BlocPaiement = z
  .object({
    ...communBloc,
    bloc: z.literal('paiement'),
    moment: z.enum(['immediat', 'apres_approbation']).default('immediat'),
    delai_minutes: z.number().int().min(1).default(30),
  })
  .strict()

export const BlocDepot = z
  .object({
    ...communBloc,
    bloc: z.literal('depot'),
    delai_minutes: z.number().int().min(1).default(30),
  })
  .strict()

export const BlocNotification = z
  .object({
    ...communBloc,
    bloc: z.literal('notification'),
    destinataire: z.enum(['citoyen', 'responsable']).default('citoyen'),
    sujet: z.string(),
    corps: z.string(),
    /** Décalage par rapport au début de la réservation, en heures. Négatif = avant. */
    decalage_heures: z.number().int().optional(),
  })
  .strict()

export const BlocConfirmation = z
  .object({
    ...communBloc,
    bloc: z.literal('confirmation'),
    message: z.string().default('Votre réservation est confirmée.'),
    /** Joint le fichier .ics au courriel de confirmation. */
    joint_calendrier: z.boolean().default(true),
  })
  .strict()

export const Bloc = z.discriminatedUnion('bloc', [
  BlocCreneau,
  BlocIdentite,
  BlocQuestions,
  BlocOffres,
  BlocContrat,
  BlocSignature,
  BlocApprobation,
  BlocPaiement,
  BlocDepot,
  BlocNotification,
  BlocConfirmation,
])
export type Bloc = z.infer<typeof Bloc>
export type TypeBloc = Bloc['bloc']

/** Les blocs que le citoyen traverse en ligne, dans l'ordre du document. */
export const BLOCS_CITOYEN: TypeBloc[] = [
  'creneau',
  'identite',
  'questions',
  'offres',
  'contrat',
  'signature',
  'paiement',
  'depot',
  'confirmation',
]
export const BLOCS_ASYNCHRONES: TypeBloc[] = ['approbation']
export const BLOCS_AUTOMATIQUES: TypeBloc[] = ['notification']

// ─── Intégrations ────────────────────────────────────────────────────────────

export const Integrations = z
  .object({
    courriel: z
      .object({
        mode: z.enum(['simule', 'reel']).default('simule'),
        repondre_a: z.string().email().optional(),
      })
      .strict()
      .prefault({}),
    paiement: z
      .object({
        mode: z.enum(['simule', 'reel']).default('simule'),
        fournisseur: z.enum(['stripe']).default('stripe'),
        devise: z.enum(['CAD']).default('CAD'),
      })
      .strict()
      .prefault({}),
    signature: z
      .object({ mode: z.enum(['interne']).default('interne') })
      .strict()
      .prefault({}),
    calendrier: z
      .object({ ics: z.boolean().default(true) })
      .strict()
      .prefault({}),
  })
  .strict()
export type Integrations = z.infer<typeof Integrations>

// ─── Service public et page ──────────────────────────────────────────────────

export const Page = z
  .object({
    /** Paragraphe d'accroche, au-dessus de tout le reste. */
    intro: z.string().optional(),
    sections: z
      .array(
        z
          .object({ titre: z.string().min(1), texte: z.string().min(1) })
          .strict(),
      )
      .default([]),
    afficher_horaires: z.boolean().default(true),
    afficher_tarifs: z.boolean().default(true),
    afficher_regles: z.boolean().default(true),
    afficher_contact: z.boolean().default(true),
    /** Le client a déjà écrit sa propre présentation dans son site : on n'injecte que le calendrier. */
    contenu_fourni_par_lhote: z.boolean().default(false),
  })
  .strict()

export const ServicePublic = z
  .object({
    cle: Cle,
    nom: z.string().min(1),
    /** Un lien nominatif émis par l'administration est requis pour démarrer. */
    acces_sur_invitation: z.boolean().optional(),
    /** Une demande administrative ne réserve aucune capacité ni plage horaire. */
    mode_reservation: z
      .enum(['creneau', 'evenement', 'demande', 'achat'])
      .optional(),
    inventaire_item_id: z.string().optional(),
    /** Date du modèle, conservée même si le brouillon n'a pas encore son étape. */
    periode_evenement: z
      .object({ debut_utc: z.iso.datetime(), fin_utc: z.iso.datetime() })
      .strict()
      .optional(),
    titre: z.string().optional(),
    description: z.string().optional(),
    lieu: z
      .object({
        nom: z.string().min(1),
        adresse: z.string().optional(),
        note: z.string().optional(),
      })
      .strict()
      .optional(),
    contact: z
      .object({
        courriel: z.string().email().optional(),
        telephone: z.string().optional(),
        nom: z.string().optional(),
      })
      .strict()
      .optional(),
    fuseau: z.string().default('America/Toronto'),
  })
  .strict()

// ─── Le document ─────────────────────────────────────────────────────────────

export const Configuration = z
  .object({
    version_grammaire: z.literal(1),
    service: ServicePublic,
    ressources: z.array(Ressource),
    regles: z.array(Regle).default([]),
    processus: z
      .object({ blocs: z.array(Bloc), graphe: GrapheParcours.optional() })
      .strict(),
    integrations: Integrations.prefault({}),
    page: Page.prefault({}),
  })
  .strict()
  .superRefine((c, ctx) => {
    if (
      !['demande', 'achat'].includes(c.service.mode_reservation ?? '') &&
      c.ressources.length === 0
    )
      ctx.addIssue({
        code: 'custom',
        path: ['ressources'],
        message: 'Une réservation demande au moins une ressource.',
      })
  })
export type Configuration = z.infer<typeof Configuration>
/** Le document tel qu'écrit à la main ou par le modèle, avant application des défauts. */
export type ConfigurationBrute = z.input<typeof Configuration>
